import React, { useState, useEffect } from "react";
import {
  Box,
  Text,
  VStack,
  Flex,
  Tooltip,
  Link,
  IconButton,
  Button,
} from "@chakra-ui/react";
import { DeleteIcon, ExternalLinkIcon } from "@chakra-ui/icons";
import { Header } from "build-ts/metadata";
import { MetadataTypeEnum, metadataTypeEnumFromJSON } from "build-ts/enums";
import CustomModal from "../modals/CustomModal";
import { deleteAnalysisTreeByID } from "services/AnalysisTreeServices";

interface DependenciesModalProps {
  isOpen: boolean;
  onClose: () => void;
  dependencies: Header[];
  onViewMKG?: (metric: Header) => void;
}

const DependenciesModal: React.FC<DependenciesModalProps> = ({
  isOpen,
  onClose,
  dependencies,
  onViewMKG,
}) => {
  // Local state to manage the dependencies list
  const [localDependencies, setLocalDependencies] = useState<Header[]>(dependencies);

  // Update local state when dependencies prop changes
  useEffect(() => {
    setLocalDependencies(dependencies);
  }, [dependencies]);

  // Simple filtering for each type using local state
  const metrics = localDependencies.filter(dep => metadataTypeEnumFromJSON(dep.type) === MetadataTypeEnum.METRIC);
  const plans = localDependencies.filter(dep => metadataTypeEnumFromJSON(dep.type) === MetadataTypeEnum.ANALYSIS_PLAN);
  const analyses = localDependencies.filter(dep => metadataTypeEnumFromJSON(dep.type) === MetadataTypeEnum.ANALYSIS_TREE);
  const benchmarks = localDependencies.filter(dep => metadataTypeEnumFromJSON(dep.type) === MetadataTypeEnum.BENCHMARK);
  const analysts = localDependencies.filter(dep => metadataTypeEnumFromJSON(dep.type) === MetadataTypeEnum.ANALYST);

  const handleDeleteAnalysis = async (id: string) => {
    await deleteAnalysisTreeByID(id);
    // Remove from local list after successful deletion
    setLocalDependencies(prev => prev.filter(dep => dep.id !== id));
  };

  return (
    <CustomModal
      isOpen={isOpen}
      onClose={onClose}
      title="Dependency Confirmation"
      maxWidth="90vw"
      maxHeight="90vh"
      isCentered={true}
      motionPreset="scale"
      overlayBg="blackAlpha.300"
    >
      <Box p={4}>
        {/* Dependent Analysts */}
        {analysts.length > 0 && (
          <>
            <Text fontSize="xl" mb={4} fontWeight="bold">
              These are the dependent analysts:
            </Text>
            <VStack align="stretch" spacing={2}>
              {analysts.map(analyst => (
                <Flex justify="space-between" key={analyst.id} pl={5} pr={20}>
                  <Text fontStyle="italic">{analyst.name}</Text>
                  <Box>
                    <Tooltip label="Show Analyst">
                      <Link href={`/analyst/${analyst?.id}`} isExternal>
                        <IconButton
                          aria-label="Show analyst"
                          pl={4}
                          icon={<ExternalLinkIcon/>}
                          size="md"
                          bg="none"
                          color="green"
                        />
                      </Link>
                    </Tooltip>
                  </Box>
                </Flex>
              ))}
            </VStack>
          </>
        )}

        {/* Dependent Metrics */}
        {metrics.length > 0 && (
          <>
            <Text fontSize="xl" mb={4} mt={analysts.length > 0 ? 6 : 0} fontWeight="bold">
              These are the dependent metrics:
            </Text>
            <VStack align="stretch" spacing={2}>
              {metrics.map(metric => (
                <Flex justify="space-between" key={metric.id} pl={5} pr={20}>
                  <Text fontStyle="italic">{metric.name}</Text>
                  <Box>
                    {onViewMKG && (
                      <Button
                        size="sm"
                        colorScheme="purple"
                        bg="purple.50"
                        color="black"
                        borderRadius="xl"
                        border="1px solid"
                        borderColor="#4e1e6c"
                        _hover={{ bg: "#4e1e6c", color: "white" }}
                        onClick={() => onViewMKG(metric)}
                      >
                        View MKG
                      </Button>
                    )}
                  </Box>
                </Flex>
              ))}
            </VStack>
          </>
        )}

        {/* Dependent Plans */}
        {plans.length > 0 && (
          <>
            <Text fontSize="xl" mb={4} mt={(analysts.length > 0 || metrics.length > 0) ? 6 : 0} fontWeight="bold">
              These are the dependent plans:
            </Text>
            <VStack align="stretch" spacing={2}>
              {plans.map(plan => (
                <Flex justify="space-between" key={plan.id} pl={5} pr={20}>
                  <Text fontStyle="italic">{plan.name}</Text>
                  <Box>
                    <Tooltip label="Show Plan">
                      <Link href={`/plan?metadataId=${plan?.id}&metadataType=ANALYSIS_PLAN`} isExternal>
                        <IconButton
                          aria-label="Show analysis"
                          pl={4}
                          icon={<ExternalLinkIcon/>}
                          size="md"
                          bg="none"
                          color="green"
                        />
                      </Link>
                    </Tooltip>
                  </Box>
                </Flex>
              ))}
            </VStack>
          </>
        )}

        {/* Dependent Analysis */}
        {analyses.length > 0 && (
          <>
            <Text fontSize="xl" mb={4} mt={(analysts.length > 0 || metrics.length > 0 || plans.length > 0) ? 6 : 0} fontWeight="bold">
              These are the dependent analyses:
            </Text>
            <VStack align="stretch" spacing={2}>
              {analyses.map(analysis => (
                <Flex justify="space-between" key={analysis.id} pl={5} pr={20}>
                  <Text fontStyle="italic">{analysis.name}</Text>
                  <Box>
                    <Tooltip label="Show analysis">
                      <Link href={`/analyze/${analysis.id}`} isExternal>
                        <IconButton
                          aria-label="Show analysis"
                          pl={4}
                          icon={<ExternalLinkIcon/>}
                          size="md"
                          bg="none"
                          color="green"
                        />
                      </Link>
                    </Tooltip>
                    <Tooltip label="Delete analysis">
                      <IconButton
                        aria-label="Delete analysis"
                        pl={4}
                        icon={<DeleteIcon />}
                        size="md"
                        bg="none"
                        color="grey"
                        onClick={() => handleDeleteAnalysis(analysis.id!)} 
                      />
                    </Tooltip>
                  </Box>
                </Flex>
              ))}
            </VStack>
          </>
        )}

        {/* Dependent Benchmarks */}
        {benchmarks.length > 0 && (
          <>
            <Text fontSize="xl" mb={4} mt={(analysts.length > 0 || metrics.length > 0 || plans.length > 0 || analyses.length > 0) ? 6 : 0} fontWeight="bold">
              These are the dependent benchmarks:
            </Text>
            <VStack align="stretch" spacing={2}>
              {benchmarks.map(benchmark => (
                <Flex justify="space-between" key={benchmark.id} pl={5} pr={20}>
                  <Text fontStyle="italic">{benchmark.name}</Text>
                </Flex>
              ))}
            </VStack>
          </>
        )}
      </Box>
    </CustomModal>
  );
};

export default DependenciesModal; 